import styled from 'styled-components'
import { ErrorPage } from '@/features/shared/components/ErrorPage'
import { SearchFilterOptions } from '@/features/shop/components/ProductFilter/FilterOptions/SearchFilterOptions'
import { SearchProductsRenderer } from '@/features/shop/components/Search/SearchProductsRenderer'
import { SearchEmptyState } from '@/features/shop/components/SearchEmptyState'
import { SearchGridLayer } from '@/features/shop/components/SearchGridLayer'
import { SearchResultsText } from '@/features/shop/components/SearchResultsText'
import { SkeletonProvider } from '@/features/shared/components/SkeletonLoader'
import { LoadingIndicator } from '@shipt/design-system-loading'
import { useInfiniteQuerySearch } from '@/features/shop/services/Search/queries'
import { SearchType } from '@/features/shop/services/Search/types'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { useEffect } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { SEARCH_CORE_RESULTS_DURATION, SEARCH_PAGE_RESULTS_DURATION } from '@/lib/datadog/vitals'
import { useIsBrowse } from '@/features/shop/services/Search/hooks'
import { Box, Row } from '@shipt/design-system-layouts'
import {
  useIsGuestUser,
  useUserStoreImage,
  useUserStoreName,
} from '@/features/account/services/User/hooks'
import { SearchRedirect } from '@/features/shop/components/SearchRedirect'
import {
  useQuerySearchCMSAdBanner,
  useQuerySearchCMSAdShowcase,
  useQueryZeroSearchResultsCMSShelves,
  useQuerySearchAdVideoShowcase,
} from '@/features/cms/services/CMS/queries'
import { LayoutGroup } from '@/features/cms/components/CMS/components/LayoutGroup'
import { SEOHead } from '@/features/shared/components/SEOHeader/SEOHead'
import { useParams } from 'next/navigation'
import { SEO_PAGE_TYPE } from '@/features/shared/components/SEOHeader/types'
import { useSearchQueryParams, useValidSearchParams } from '@/features/shared/hooks/search'
import { getCanonicalUrl } from '@/features/shared/utils/seo/seoConfig'
import {
  getRetailerShopBreadcrumbs,
  getRetailerShopTitle,
  getStoreCategory,
} from '@/features/shop/utils/seo'
import { C360NoMarkupBanner } from '@/features/entrypoint/components/Circle360/C360NoMarkupBanner'
import { type ParamsOf } from '@next-types/routes'
import { useIsEligibleForC360NoMarkup } from '@/features/entrypoint/components/Circle360/useIsEligibleForC360NoMarkup'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'
import { getBuyAgainShelf } from '@/features/shop/services/Search/utils'
import { getErrorStatus } from '@/features/shared/utils/dataFetching/utils'
import { type AdShowcaseData, type AdVideoShowcaseData } from '@/features/cms/components/CMS/types'

export const SearchContainer = () => {
  const params = useParams<ParamsOf<'/shop/[store_slug]'>>()
  const storeCategory = getStoreCategory(params?.store_slug)
  const storeName = useUserStoreName()
  const { image: storeImage } = useUserStoreImage()

  const storeSlug = params?.store_slug
  const canonicalUrl = getCanonicalUrl(`/shop/${storeSlug}/search`)
  const pageTitle = getRetailerShopTitle(storeSlug, storeName)
  const pageDescription = `Enjoy ${storeName} same-day ${storeCategory.toLowerCase()} in as little as one hour with Shipt. Shop online now & have your favorite ${storeName} products brought right to your door.`

  const breadcrumbs = getRetailerShopBreadcrumbs(params?.store_slug, storeName, [
    { name: pageTitle, item: canonicalUrl },
  ])

  const pageTypeSchema = {
    '@context': 'https://schema.org',
    '@type': [SEO_PAGE_TYPE.SEARCH_RESULTS_PAGE, SEO_PAGE_TYPE.COLLECTION_PAGE],
    name: pageTitle,
    description: pageDescription,
    url: canonicalUrl,
    image: {
      url: objectStorageUrl('get-started-hero.png'),
      width: 872,
      height: 1072,
    },
    mainEntity: {
      '@type': SEO_PAGE_TYPE.LOCAL_BUSINESS,
      name: storeName,
      url: canonicalUrl,
      logo: storeImage,
    },
  }

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        breadcrumbs={breadcrumbs}
        pageTypeSchema={pageTypeSchema}
      />
      <SearchContainerContent />
    </>
  )
}

const SearchContainerContent = () => {
  const isBrowse = useIsBrowse()
  const routerQuery = useSearchQueryParams()
  const searchType = String(routerQuery.searchType ?? '')
  const { query = '', category_name = '', category_id } = useValidSearchParams()
  const infiniteQueryResults = useInfiniteQuerySearch({
    shouldTrack: true,
    searchType,
  })
  const { error, isFetched, isPending, isFetching, searchResults, isFetchingNextPage } =
    infiniteQueryResults
  const { total_hits, facets, metadata } = searchResults
  const searchInProgress = isFetching && !isFetchingNextPage
  const isGuest = useIsGuestUser()
  const { data: zeroResultsShelves } = useQueryZeroSearchResultsCMSShelves(
    query,
    isFetched && total_hits === 0,
  )

  const { data: searchAdBanner, isFetching: isFetchingAdBanner } = useQuerySearchCMSAdBanner({
    query,
    category_id,
  })
  const { data: searchShelfShowCase, isFetching: isFetchingAdShowcase } =
    useQuerySearchCMSAdShowcase({
      query,
      category_id,
    })
  const { data: adVideoShowCase, isFetching: isFetchingAdVideoShowcase } =
    useQuerySearchAdVideoShowcase({ query: isBrowse ? '' : query })

  const isEligibleForC360NoMarkup = useIsEligibleForC360NoMarkup()

  const showBuyAgainShelf = isFetched && !!getBuyAgainShelf(searchResults.shelves)

  const adShowcase = (() => {
    // browse pages only render the non video showcase
    if (isBrowse) return searchShelfShowCase
    if (isFetchingAdVideoShowcase) return
    // if both ad video showcase and non video showcase are available, prioritize video
    if (adVideoShowCase?.data) return adVideoShowCase
    if (searchShelfShowCase?.data) return searchShelfShowCase
  })()

  const topSkinnyShowcase =
    !isBrowse && !showBuyAgainShelf
      ? searchAdBanner?.data?.children?.find(
          (child): child is AdShowcaseData => child.content_type_id === 'ad_showcase',
        )
      : undefined

  const topSkinnyShowcaseKey = topSkinnyShowcase ? getAdCampaignKey(topSkinnyShowcase) : undefined
  const adShowcaseKey = adShowcase ? getAdCampaignKey(adShowcase) : undefined
  const isDuplicateAdShowcase = !!topSkinnyShowcaseKey && topSkinnyShowcaseKey === adShowcaseKey
  const adShowcaseToRender = isDuplicateAdShowcase ? undefined : adShowcase

  const adBanner = (() => {
    if (!searchAdBanner?.data?.children?.length) return undefined

    // The top slot uses the CMS Skinny Showcase independently of the grid showcase.
    const adBannerChildrenToRender = searchAdBanner.data.children.filter((child) =>
      topSkinnyShowcase
        ? child.content_type_id === 'ad_showcase'
        : child.content_type_id === 'ad_banner',
    )

    if (!adBannerChildrenToRender.length) return undefined

    return {
      ...searchAdBanner,
      data: {
        ...searchAdBanner.data,
        children: adBannerChildrenToRender,
      },
    }
  })()

  const coreResultsReady = !isBrowse && isFetched
  const pageReady =
    coreResultsReady && !isFetchingAdBanner && !isFetchingAdShowcase && !isFetchingAdVideoShowcase

  useEffect(() => {
    if (coreResultsReady) {
      datadogRum.stopDurationVital(SEARCH_CORE_RESULTS_DURATION, {
        context: { totalHits: total_hits },
      })
    }
  }, [coreResultsReady, total_hits])

  const adBannerPresent = !!adBanner
  const adShowcasePresent = !!adShowcaseToRender
  const adVideoShowcasePresent = !!adVideoShowCase?.data

  useEffect(() => {
    if (pageReady) {
      datadogRum.stopDurationVital(SEARCH_PAGE_RESULTS_DURATION, {
        context: {
          totalHits: total_hits,
          adBannerPresent,
          adShowcasePresent,
          adVideoShowcasePresent,
        },
      })
    }
  }, [pageReady, total_hits, adBannerPresent, adShowcasePresent, adVideoShowcasePresent])

  const is401Error = getErrorStatus(error) === 401

  if (error && !is401Error) return <ErrorPage />
  if (isPending || (total_hits === 0 && isFetching))
    return (
      <Row align="center" justify="center" marginTop="5xl">
        <LoadingIndicator size="xl" />
      </Row>
    )
  if (metadata?.status === 'redirect' && metadata.redirect_url)
    return <SearchRedirect url={metadata.redirect_url} />
  if (!total_hits) {
    return (
      <GridLayoutContainer>
        <SearchEmptyState query={query || category_name} />
        {!!zeroResultsShelves && <LayoutGroup {...zeroResultsShelves} />}
      </GridLayoutContainer>
    )
  }
  return (
    <SkeletonProvider isLoading={searchInProgress}>
      {!isBrowse && !!isEligibleForC360NoMarkup && <C360NoMarkupBanner />}
      <GridLayoutContainer contentMaxWidth>
        {!!adBanner && (
          <AdBannerContainer className="fullWidth">
            <LayoutGroup {...adBanner} />
          </AdBannerContainer>
        )}
        {isBrowse ? (
          <SearchGridLayer facets={facets} total_hits={total_hits} isFetching={isFetching}>
            <SearchProductsRenderer
              infiniteQueryResults={infiniteQueryResults}
              showAddToListButton={!isGuest}
              adShowcase={adShowcaseToRender}
              showLoginBanner={is401Error}
            />
          </SearchGridLayer>
        ) : (
          <>
            <ScrollToTopOnQueryChange key={query} />
            <SearchFilterOptions
              facets={facets}
              searchType={SearchType.SEARCH}
              className="contentAtTablet"
            />
            <Box margin={[0, 0, 'lg']}>
              <SearchResultsText totalResults={total_hits} />
            </Box>
            <SearchProductsRenderer
              infiniteQueryResults={infiniteQueryResults}
              showLargerProducts
              showAddToListButton={!isGuest}
              isSearchGrid
              adShowcase={adShowcaseToRender}
            />
          </>
        )}
      </GridLayoutContainer>
    </SkeletonProvider>
  )
}

const ScrollToTopOnQueryChange = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])
  return null
}

const getAdCampaignKey = (ad: AdShowcaseData | AdVideoShowcaseData) => {
  const { source_object, featuredTransactionID } = ad.metadata.read_operation_info ?? {}
  const promotion = source_object?.object

  if (promotion?.promotion_id) return `promotion:${promotion.promotion_id}`
  if (promotion?.id !== null && promotion?.id !== undefined) {
    return `campaign:${promotion.id}`
  }
  if (featuredTransactionID) return `transaction:${featuredTransactionID}`
}

const AdBannerContainer = styled.div`
  margin-top: 16px;
`
